import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Video, Hash, Send, Trophy, Flame, ArrowRight, Mountain, BookOpen, Sprout, Sparkles, CalendarClock } from "lucide-react";
import { api } from "@/lib/api";
import { prefersReducedMotion } from "@/lib/anim";
import { CAMPAIGN } from "@/lib/images";
import { SiteNav } from "@/components/public/SiteNav";
import { SiteFooter } from "@/components/public/SiteFooter";
import { Hero } from "@/components/public/Hero";
import { Marquee } from "@/components/landing/Marquee";
import { Countdown } from "@/components/public/Countdown";

const PRIZES = [
  { place: "১ম", en: "1ST PRIZE", title: "মোটরবাইক", sub: "Brand-new Motorbike", img: CAMPAIGN.prizeBike, accent: "#F42A41", big: true },
  { place: "২য়", en: "2ND PRIZE", title: "iPhone 17 Pro Max", sub: "Flagship Phone", img: CAMPAIGN.prizePhone, accent: "#E0A52E" },
  { place: "৩য়", en: "3RD PRIZE", title: "ক্যামেরা ড্রোন", sub: "Camera Drone", img: CAMPAIGN.prizeDrone, accent: "#006A4E" },
  { place: "৪র্থ", en: "4TH PRIZE", title: "Android স্মার্টফোন", sub: "Android Phone", img: CAMPAIGN.prizeAndroid, accent: "#006A4E" },
  { place: "৫ম", en: "5TH PRIZE", title: "Android স্মার্টফোন", sub: "Android Phone", img: CAMPAIGN.prizeAndroid2, accent: "#006A4E" },
];

const STEPS = [
  { icon: Video, n: "০১", bn: "রিল বানাও", en: "ফোন দিয়ে সিলেট নিয়ে একটি ভার্টিকাল রিল বানাও।" },
  { icon: Hash, n: "০২", bn: "পোস্ট করো #madeinsylhet ও #fahimalchoudhury দিয়ে", en: "পাবলিকভাবে পোস্ট করো #madeinsylhet আর #fahimalchoudhury হ্যাশট্যাগ দিয়ে।" },
  { icon: Send, n: "০৩", bn: "রেজিস্টার করো", en: "এখানে তোমার নাম আর রিলের লিংক জমা দাও — ব্যস!" },
];

const TOPICS = [
  { icon: Mountain, bn: "সিলেটের প্রাকৃতিক সৌন্দর্য", accent: "#006A4E" },
  { icon: BookOpen, bn: "শিক্ষা ও সংস্কৃতি", accent: "#E0A52E" },
  { icon: Sprout, bn: "শিল্প, কৃষি ও মানুষের জীবন", accent: "#7FB59B" },
  { icon: Sparkles, bn: "এমন গল্প, যা দেখলেই মনে হয় — “এটাই সিলেট”", accent: "#F42A41" },
];

function PrizeCard({ p, featured }) {
  return (
    <div data-pop className={`group relative rounded-3xl overflow-hidden border border-white/10 bg-zinc-950 ${featured ? "lg:row-span-2" : ""}`}>
      <div className="absolute inset-0" style={{ background: `radial-gradient(120% 80% at 50% 0%, ${p.accent}22, transparent 60%)` }} />
      <div className={`relative ${featured ? "aspect-[4/5] lg:aspect-auto lg:h-[58%]" : "aspect-square"} overflow-hidden`}>
        <img src={p.img} alt={p.sub} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        <span className="absolute top-4 left-4 rounded-full px-3 py-1 text-xs font-bold tracking-wider text-black" style={{ background: p.accent }}>{p.en}</span>
      </div>
      <div className="relative p-6 md:p-7">
        <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-[0.2em]">
          <Trophy size={13} style={{ color: p.accent }} /> <span className="font-bn">{p.place} পুরস্কার</span>
        </div>
        <h3 className={`font-bn font-bold text-cream mt-2 ${featured ? "text-4xl md:text-5xl" : "text-2xl"}`}>{p.title}</h3>
        <p className="text-white/45 text-sm mt-1">{p.sub}</p>
      </div>
    </div>
  );
}

export default function Landing() {
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const prizesRef = useRef(null);

  useEffect(() => {
    api.get("/public/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const daysLeft = settings?.deadline
    ? Math.ceil((new Date(settings.deadline).getTime() - Date.now()) / 86400000)
    : null;
  const urgent = daysLeft != null && daysLeft > 0 && daysLeft <= 7;

  // Reliable pop-in for [data-pop] (reduced-motion leaves content visible)
  useEffect(() => {
    if (prefersReducedMotion()) return;
    const els = Array.from(document.querySelectorAll("[data-pop]"));
    els.forEach((el) => {
      el.style.opacity = "0";
      el.style.transform = "translateY(48px) scale(0.95)";
      el.style.transition = "opacity .7s cubic-bezier(.22,1,.36,1), transform .7s cubic-bezier(.22,1,.36,1)";
    });
    const show = (el) => { el.style.opacity = "1"; el.style.transform = "none"; };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { show(e.target); io.unobserve(e.target); } });
    }, { threshold: 0.12 });
    els.forEach((el) => {
      if (el.getBoundingClientRect().top < window.innerHeight * 0.92) show(el);
      else io.observe(el);
    });
    return () => io.disconnect();
  }, []);

  return (
    <div className="bg-[#0a0a0b]">
      <SiteNav />
      <Hero stats={stats} />

      {/* 2 — PRIZES (the star) */}
      <section id="prizes" ref={prizesRef} className="bg-[#0a0a0b] py-24 md:py-32" data-testid="prizes-section">
        <div className="max-w-content mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <span className="inline-flex items-center gap-2 text-sylhet-gold text-xs font-bold uppercase tracking-[0.25em]"><Flame size={14} /> Win Big</span>
              <h2 className="font-bn font-bold text-cream text-4xl md:text-7xl tracking-tight mt-3 leading-[0.95]">
                জিতে নাও <span className="text-sylhet-gold">দারুণ</span> পুরস্কার
              </h2>
            </div>
            <p className="text-white/55 max-w-xs font-bn text-lg">৫টি বড় পুরস্কার অপেক্ষা করছে সেরা রিল-নির্মাতাদের জন্য।</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 lg:auto-rows-[1fr]">
            <div className="lg:col-span-1"><PrizeCard p={PRIZES[0]} featured /></div>
            <div className="lg:col-span-2 grid sm:grid-cols-2 gap-5">
              {PRIZES.slice(1).map((p, i) => <PrizeCard key={i} p={p} />)}
            </div>
          </div>
        </div>
      </section>

      {/* 3 — STEPS */}
      <section className="bg-sylhet-green text-cream py-24 md:py-32" data-testid="steps-section">
        <div className="max-w-content mx-auto px-6 md:px-12">
          <h2 className="font-bn font-bold text-4xl md:text-7xl tracking-tight mb-14 leading-[0.95]">মাত্র <span className="text-sylhet-gold">৩ ধাপে</span> অংশ নাও</h2>
          <div className="grid md:grid-cols-3 gap-5">
            {STEPS.map((s, i) => (
              <div key={i} data-pop className="rounded-3xl bg-black/20 border border-cream/15 p-8 hover:bg-black/30 transition-colors">
                <div className="flex items-center justify-between">
                  <span className="w-14 h-14 rounded-2xl bg-sylhet-gold text-black grid place-items-center"><s.icon size={26} /></span>
                  <span className="font-condensed text-5xl text-cream/15">{s.n}</span>
                </div>
                <h3 className="font-bn font-bold text-2xl mt-6">{s.bn}</h3>
                <p className="text-cream/65 mt-2">{s.en}</p>
              </div>
            ))}
          </div>
          <Link to="/submit" data-testid="steps-cta" className="inline-flex items-center gap-2 mt-12 rounded-full bg-sylhet-red text-cream font-bn font-bold text-lg px-8 py-4 hover:-translate-y-1 transition-transform">
            শুরু করো এখনই <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      {/* 4 — TOPICS + RULES */}
      <section className="bg-[#0a0a0b] py-24 md:py-32" data-testid="topics-section">
        <div className="max-w-content mx-auto px-6 md:px-12">
          <div className="mb-12">
            <span className="inline-flex items-center gap-2 text-sylhet-gold text-xs font-bold uppercase tracking-[0.25em]"><Sparkles size={14} /> Themes</span>
            <h2 className="font-bn font-bold text-cream text-4xl md:text-7xl tracking-tight mt-3 leading-[0.95]">কী নিয়ে <span className="text-sylhet-gold">রিল</span> বানাবে?</h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {TOPICS.map((t, i) => (
              <div key={i} data-pop data-testid={`topic-card-${i}`} className="group rounded-3xl border border-white/10 bg-zinc-950 p-7 hover:border-white/25 transition-colors">
                <span className="w-14 h-14 rounded-2xl grid place-items-center" style={{ background: `${t.accent}1f`, color: t.accent }}><t.icon size={26} /></span>
                <h3 className="font-bn font-bold text-cream text-2xl mt-6 leading-snug">{t.bn}</h3>
              </div>
            ))}
          </div>

          <div data-pop data-testid="rules-callout" className="mt-10 rounded-3xl border-2 border-sylhet-red/60 bg-sylhet-red/10 p-7 md:p-9 flex items-start gap-4">
            <span className="shrink-0 w-12 h-12 rounded-2xl bg-sylhet-gold text-black grid place-items-center font-bold text-xl">!</span>
            <p className="font-bn font-semibold text-cream text-xl md:text-2xl leading-snug">
              মনে রাখো: কোনো রাজনৈতিক বা ব্যক্তিগত প্রচার নয় — <span className="text-sylhet-gold">সম্পূর্ণ নিরপেক্ষ কনটেন্ট।</span>
            </p>
          </div>
        </div>
      </section>

      {/* 5 — REEL SHOWCASE + marquee */}
      <section className="bg-[#0a0a0b] py-24 md:py-32 overflow-hidden" data-testid="showcase-section">
        <div className="max-w-content mx-auto px-6 md:px-12 mb-12">
          <span className="text-sylhet-red text-xs font-bold uppercase tracking-[0.25em]">Be Seen</span>
          <h2 className="font-bn font-bold text-cream text-4xl md:text-7xl tracking-tight mt-3 leading-[0.95]">সারা সিলেট <span className="text-sylhet-red">দেখবে</span> তোমার রিল</h2>
        </div>
        <div className="max-w-content mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-5 gap-4">
          {CAMPAIGN.creators.map((src, i) => (
            <div key={i} data-pop className={`relative rounded-2xl overflow-hidden aspect-[9/16] border border-white/10 ${i % 2 ? "md:translate-y-6" : ""}`}>
              <img src={src} alt="" loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <span className="absolute bottom-2 left-2 text-cream/90 text-xs font-bn">#madeinsylhet</span>
            </div>
          ))}
        </div>
        <div className="mt-14 bg-sylhet-gold text-black py-3">
          <Marquee items={["#madeinsylhet", "✦", "#fahimalchoudhury", "✦", "তোমার রিল তোমার মঞ্চ", "✦"]} itemClassName="font-condensed text-2xl px-6 uppercase" />
        </div>
      </section>

      {/* 5 — MANIFESTO */}
      <section className="bg-[#0a0a0b] py-24 md:py-36" data-testid="manifesto-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <p data-pop className="font-bn font-bold text-cream text-3xl md:text-6xl leading-[1.15] tracking-tight text-balance">
            তুমি যেখানেই থাকো — অলিগলি, চা-বাগান, কিংবা শহরের ব্যস্ত মোড় — <span className="text-sylhet-red">তোমার গল্প</span> বলার সময় এখন।
            ক্যামেরা তোমার হাতে, <span className="text-sylhet-gold">মঞ্চ</span> তোমার সামনে।
          </p>
          <p data-pop className="font-condensed text-cream/40 text-2xl md:text-4xl uppercase mt-10">Just one reel can change everything.</p>
        </div>
      </section>

      {/* 6 — COUNTDOWN */}
      <section className="bg-sylhet-red text-cream py-20 md:py-28" data-testid="countdown-section">
        <div className="max-w-content mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            {urgent ? (
              <>
                <span className="inline-flex items-center gap-2 text-cream/80 text-xs font-bold uppercase tracking-[0.25em]"><Flame size={14} /> Hurry</span>
                <h2 data-testid="countdown-heading" className="font-bn font-bold text-4xl md:text-6xl mt-3 leading-[0.95]">সময় ফুরিয়ে আসছে!</h2>
                <p className="font-bn text-cream/85 mt-4 text-lg">শেষ তারিখের আগেই তোমার রিল জমা দাও।</p>
              </>
            ) : (
              <>
                <span className="inline-flex items-center gap-2 text-cream/80 text-xs font-bold uppercase tracking-[0.25em]"><CalendarClock size={14} /> Deadline</span>
                <h2 data-testid="countdown-heading" className="font-bn font-bold text-4xl md:text-6xl mt-3 leading-[0.95]">শেষ তারিখ</h2>
                <p className="font-bn text-cream/85 mt-4 text-lg">নির্ধারিত সময়ের মধ্যে তোমার রিল জমা দাও।</p>
              </>
            )}
            <Link to="/submit" data-testid="countdown-cta" className="inline-flex items-center gap-2 mt-7 rounded-full bg-black text-cream font-bn font-bold text-lg px-8 py-4 hover:-translate-y-1 transition-transform">
              রিল জমা দাও <ArrowRight size={20} />
            </Link>
          </div>
          <div className="md:col-span-6 md:flex md:justify-end">
            <div className="rounded-3xl bg-black/25 border border-cream/20 p-8">
              <div className="text-cream/70 text-xs uppercase tracking-[0.2em] mb-4">Deadline · Asia/Dhaka</div>
              <Countdown deadline={settings?.deadline} light />
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
